import styled from "@emotion/styled";
import { FC, ReactNode } from "react";

import {
  Button,
  ButtonProps,
  Heading,
  Icon,
  IconSource,
  Modal,
  ModalHeader,
  SpacingToken,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { withVar } from "themes/helpers";

type IModalButtonProps = {
  label: string;
  dataTestId?: string;
  hideButton?: boolean;
} & Omit<ButtonProps, "children" | "size">;

interface IProps {
  children: ReactNode;
  icon?: ReactNode | { source: IconSource };
  hideCancelButton?: boolean;
  heading: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmButton?: IModalButtonProps;
  cancelButton?: IModalButtonProps;
}

export const ConfirmModal: FC<IProps> = ({
  children,
  icon,
  heading,
  onCancel,
  onConfirm,
  hideCancelButton,
  confirmButton = { label: "Confirm" },
  cancelButton = { label: "Cancel" },
}) => {
  const { dataTestId: confirmTestId, label: confirmLabel, ...confirmProps } = confirmButton;
  const { dataTestId: cancelTestId, label: cancelLabel, ...cancelProps } = cancelButton;

  const isIconSource = (icon: ReactNode | { source: IconSource }): icon is { source: IconSource } => {
    return typeof icon === "object" && icon !== null && "source" in icon;
  };

  const iconElement = icon && isIconSource(icon) ? <LargeIcon source={icon.source} /> : icon;

  return (
    <Modal heading={<ModalHeader />} onClose={onCancel}>
      <Flex
        gap={withVar(SpacingToken.Spacing4)}
        column
        alignItems="center"
        paddingBottom={withVar(SpacingToken.Spacing10)}
        paddingH={withVar(SpacingToken.Spacing9)} // including the default padding of the modal, this amounts to 64px
      >
        {iconElement}
        <Flex gap={withVar(SpacingToken.Spacing3)} column alignItems="center">
          <Heading css={{ fontWeight: 600 }}>{heading}</Heading>
          <TextAlignedBlock>{children}</TextAlignedBlock>
        </Flex>
        <Flex gap={withVar(SpacingToken.Spacing2)}>
          {!hideCancelButton && (
            <Button size="large" onClick={onCancel} kind="secondary" data-testid={cancelTestId} {...cancelProps}>
              {cancelLabel}
            </Button>
          )}

          <Button size="large" onClick={onConfirm} kind="primary" data-testid={confirmTestId} {...confirmProps}>
            {confirmLabel}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

const LargeIcon = styled(Icon)`
  svg {
    width: 64px;
    height: 64px;
  }
`;

const TextAlignedBlock = styled(Block)`
  text-align: center;
  min-width: fit-content;
  max-width: 560px;
`;
